import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { VIEW_MORE_OFFSET, VIEW_MORE_ERROR, FILTER_ERROR } from "./constants";
import RatingsAndReviews from "./components/ratingsAndReviews/RatingsAndReviews";
import RatingsAndReviewsModal from "./components/ratingsAndReviewsModal/RatingsAndReviewsModal";
import getReviews from "./api/ratingsApi";
import { fireRatingsAndReviewsOpenAnalytics } from "./analytics/pageInteraction";
import getWindow from "./utils/window";
import { useLiveRegion } from "./context/liveRegion/LiveRegionContext";
import { useRuntimeConstants } from "./context/runtimeConstants/RuntimeConstantsContext";
import ReviewPolicyContainer from "./components/reviewPolicyContainer/ReviewPolicyContainer";
import useSyndicateReviewsExperiment from "./components/ratingsAndReviews/useSyndicateReviewsExperiment";
import { mapSubRatings } from "../mappers/ratings";

const SYNDICATED_FILTER = "isSyndicated:eq:false";

const DEFAULTS = {
  filterParamDefault: null,
  filterErrorParamDefault: null,
  filteredReviewCountDefault: null,
  filterValueDefault: 0,
  isFilterPendingDefault: false,
  isViewMorePendingDefault: false,
  viewMoreOffsetDefault: VIEW_MORE_OFFSET,
  ratingsApiErrorDefault: false,
  ratingsApiErrorTranslationDefault: null,
};

export default function RatingsAndReviewsApp({ screenHoverable }) {
  const {
    getTranslation,
    webContext,
    ratings: defaultRatings,
    updateRatings,
  } = useRuntimeConstants();
  const { updateLiveRegion } = useLiveRegion();

  const initialSyndicatedReviewsEligible = useRef(
    !!defaultRatings?.displayRatingsSection
  );

  const { isFiltered } = useSyndicateReviewsExperiment(
    initialSyndicatedReviewsEligible.current
  );

  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const [isRatingsModalOpen, setIsRatingsModalOpen] = useState(false);
  const [isReviewPolicyModalOpen, setIsReviewPolicyModalOpen] = useState(false);
  const [filterParam, setFilterParam] = useState(DEFAULTS.filterParamDefault);
  const [filterErrorParam, setFilterErrorParam] = useState(
    DEFAULTS.filterErrorParamDefault
  );
  const [filteredReviewCount, setFilteredReviewCount] = useState(
    DEFAULTS.filteredReviewCountDefault
  );
  const [filterValue, setFilterValue] = useState(DEFAULTS.filterValueDefault);
  const [isFilterPending, setIsFilterPending] = useState(
    DEFAULTS.isFilterPendingDefault
  );
  const [isViewMorePending, setIsViewMorePending] = useState(
    DEFAULTS.isViewMorePendingDefault
  );
  const [viewMoreOffset, setViewMoreOffset] = useState(
    DEFAULTS.viewMoreOffsetDefault
  );
  const [ratingsApiError, setRatingsApiError] = useState(
    DEFAULTS.ratingsApiErrorDefault
  );
  const [ratingsApiErrorTranslation, setRatingsApiErrorTranslation] = useState(
    DEFAULTS.ratingsApiErrorTranslationDefault
  );
  const [ratings, setRatings] = useState(defaultRatings);
  const [ratingsModalFocusOnClose, setRatingsModalFocusOnClose] =
    useState(null);
  const [reviewPolicyModalFocusOnClose, setReviewPolicyModalFocusOnClose] =
    useState(null);
  const closeRatingsModal = () => {
    setIsRatingsModalOpen(false);
    setRatingsApiError(false);
  };
  const closeReviewPolicyModal = () => {
    setIsReviewPolicyModalOpen(false);
  };
  const focusFilter = (filterParam) => {
    getWindow().document.getElementById(
      "ratingsAndReviewsModal"
    ).parentElement.scrollTop = 0;
    const filterElement = getWindow().document.getElementById(
      `ratingSelect-${filterParam}`
    );

    filterElement && filterElement.focus();
  };

  const removeFilter = () => {
    const currentFilterParam = filterParam;

    setFilterParam(DEFAULTS.filterParamDefault);
    setFilterErrorParam(DEFAULTS.filterErrorParamDefault);
    setFilteredReviewCount(DEFAULTS.filteredReviewCountDefault);
    setFilterValue(DEFAULTS.filterValueDefault);
    setIsFilterPending(DEFAULTS.isFilterPendingDefault);
    setIsViewMorePending(DEFAULTS.isViewMorePendingDefault);
    setViewMoreOffset(DEFAULTS.viewMoreOffsetDefault);
    setRatingsApiError(DEFAULTS.ratingsApiErrorDefault);
    setRatingsApiErrorTranslation(DEFAULTS.ratingsApiErrorTranslationDefault);

    if (isFiltered) {
      loadFilteredReviews();
    } else {
      setRatings(defaultRatings);
    }

    updateLiveRegion(
      getTranslation("rdp_ratings_reviews_accessibility_filter_removed")
    );

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      focusFilter(currentFilterParam);
    }, 500);
  };

  const updateReviewInMostRecentSection = (reviewId) => {
    setRatings((prev) => {
      const updateReview = (review) =>
        review?.id === reviewId ? { ...review, isReported: true } : review;

      return {
        ...prev,
        lastThree: prev.lastThree.map(updateReview),
        mostRecent: updateReview(prev.mostRecent),
      };
    });
  };

  const updateReviewInModal = (reviewId) => {
    setRatings({
      ...ratings,
      reviews: ratings.reviews.map((review) =>
        review.id === reviewId ? { ...review, isReported: true } : review
      ),
    });
  };

  const containsReviewId = (reviews, reviewId) =>
    Array.isArray(reviews)
      ? reviews.some((r) => r.id === reviewId)
      : reviews?.id === reviewId;

  const handleReportSuccess = async (reviewId, isReviewInModal) => {
    const isAUorGB = ["AU", "GB"].includes(webContext.browseCountry);

    const reviewList = isReviewInModal
      ? isAUorGB
        ? ratings.lastThree
        : ratings.mostRecent
      : ratings.reviews;

    const reviewExists = containsReviewId(reviewList, reviewId);

    if (!reviewExists) return;

    isReviewInModal
      ? updateReviewInMostRecentSection(reviewId)
      : updateReviewInModal(reviewId);
  };

  const updateStats = (stats) => ({
    ...(stats.averageOverallRating != null && {
      averageOverallRating: stats.averageOverallRating,
    }),
    ...(stats.averageOverallStarRating != null && {
      averageOverallStarRating: stats.averageOverallStarRating,
    }),
    ...(stats.totalReviewCount != null && {
      totalReviewCount: stats.totalReviewCount,
    }),
    ...(stats.ratingDistribution != null && {
      ratingDistribution: stats.ratingDistribution,
    }),
    ...(stats.percentageRecommended != null && {
      percentageRecommended: stats.percentageRecommended,
    }),
    ...(stats.secondaryRatingsAveragesOrder != null &&
      stats.secondaryRatingsAverages != null && {
        subRatings: mapSubRatings(
          stats.secondaryRatingsAveragesOrder,
          stats.secondaryRatingsAverages
        ),
      }),
  });

  const getReviewsCallback = (filterArg) => {
    const offset = filterArg ? 0 : viewMoreOffset;
    const ratingFilter = filterArg ? `Rating:eq:${filterArg}` : filterParam;

    const filters = [];

    if (isFiltered) {
      filters.push(SYNDICATED_FILTER);
    }

    if (ratingFilter) {
      filters.push(ratingFilter);
    }

    setIsFilterPending(Boolean(filterArg));
    setIsViewMorePending(!filterArg);
    setRatingsApiError(false);

    const loadingSpinner =
      getWindow().document.getElementById("loadingSpinner");

    loadingSpinner && loadingSpinner.focus();

    const promise = new Promise((resolve) => {
      getReviews({
        offset,
        ...(filters.length > 0 && {
          filter: filters,
        }),
      })
        .then(
          ({ results, totalResults, reviewsSummary, ratingsStatistics }) => {
            setRatings((currentRatings) => {
              const reviews = filterArg
                ? results
                : [...currentRatings.reviews, ...results];

              const statsUpdate =
                !ratingFilter && ratingsStatistics
                  ? updateStats(ratingsStatistics)
                  : {};

              return {
                ...currentRatings,
                reviews,
                ...(reviewsSummary != null && { reviewsSummary }),
                ...statsUpdate,
              };
            });

            setIsFilterPending(false);
            setIsViewMorePending(false);
            setViewMoreOffset(offset + VIEW_MORE_OFFSET);
            setFilterParam(ratingFilter);
            setFilterErrorParam(null);
            setFilteredReviewCount(ratingFilter ? totalResults : null);
            setFilterValue(filterArg || filterValue);

            resolve();
          }
        )
        .catch(() => {
          const error = ratingFilter
            ? {
                type: FILTER_ERROR,
                translation: getTranslation(
                  "checkout_api_timeout_dialogue_message"
                ),
              }
            : {
                type: VIEW_MORE_ERROR,
                translation: getTranslation(
                  "pdp_ratings_reviews_reviews_api_error_message"
                ),
              };

          setIsFilterPending(false);
          setIsViewMorePending(false);
          setRatingsApiError(error.type);
          setRatingsApiErrorTranslation(error.translation);
          setFilterErrorParam(ratingFilter || null);

          resolve();
        });
    });

    return promise;
  };

  const openRatingsModal = (event) => {
    setIsRatingsModalOpen(true);
    setRatingsModalFocusOnClose(event.target);
    fireRatingsAndReviewsOpenAnalytics(ratings);
  };

  const openReviewPolicyModal = (event) => {
    setIsReviewPolicyModalOpen(true);
    setReviewPolicyModalFocusOnClose(event.target);
  };

  const dismissApiError = () => {
    const currentRatingsApiError = ratingsApiError;
    setRatingsApiError(false);

    if (currentRatingsApiError === FILTER_ERROR) {
      focusFilter(filterErrorParam);
    } else {
      const viewMoreElement =
        getWindow().document.getElementById("viewMoreRatings");

      viewMoreElement && viewMoreElement.focus();
    }
  };

  const fallbackFiltering = () => {
    setRatings(defaultRatings);
  };

  const loadFilteredReviews = (ratingFilter) => {
    const filters = [SYNDICATED_FILTER];
    if (ratingFilter) {
      filters.push(ratingFilter);
    }
    return getReviews({ offset: 0, filter: filters })
      .then(({ results, totalResults, reviewsSummary, ratingsStatistics }) => {
        setRatings((currentRatings) => ({
          ...currentRatings,
          ...(!ratingFilter && {
            totalReviewCount:
              ratingsStatistics?.totalReviewCount ?? totalResults,
            averageOverallRating:
              ratingsStatistics?.averageOverallRating ??
              currentRatings.averageOverallRating,
            averageOverallStarRating:
              ratingsStatistics?.averageOverallStarRating ??
              currentRatings.averageOverallStarRating,
            ratingDistribution:
              ratingsStatistics?.ratingDistribution ??
              currentRatings.ratingDistribution,
            percentageRecommended:
              ratingsStatistics?.percentageRecommended ??
              currentRatings.percentageRecommended,
            ...(ratingsStatistics?.secondaryRatingsAveragesOrder != null &&
              ratingsStatistics?.secondaryRatingsAverages != null && {
                subRatings: mapSubRatings(
                  ratingsStatistics.secondaryRatingsAveragesOrder,
                  ratingsStatistics.secondaryRatingsAverages
                ),
              }),
          }),
          mostRecent: results[0] ?? null,
          lastThree: results.slice(0, 3),
          reviews: results,
          ...(reviewsSummary != null && { reviewsSummary }),
        }));
        setFilteredReviewCount(ratingFilter ? totalResults : null);
      })
      .catch(() => {
        fallbackFiltering();
        if (ratingFilter) {
          setFilterParam(DEFAULTS.filterParamDefault);
          setFilterValue(DEFAULTS.filterValueDefault);
          setFilteredReviewCount(DEFAULTS.filteredReviewCountDefault);
        }
      });
  };

  useEffect(() => {
    if (!isFiltered || !updateRatings) return;
    setViewMoreOffset(DEFAULTS.viewMoreOffsetDefault);
    setIsViewMorePending(DEFAULTS.isViewMorePendingDefault);
    loadFilteredReviews(filterParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFiltered, updateRatings]);

  // Sync rating stats to context when filtered (avoid rerendering the whole app on pagination-only updates)
  useEffect(() => {
    if (!isFiltered || !updateRatings) return;
    updateRatings(ratings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFiltered,
    updateRatings,
    ratings.totalReviewCount,
    ratings.averageOverallRating,
    ratings.averageOverallStarRating,
    ratings.ratingDistribution,
    ratings.percentageRecommended,
    ratings.subRatings,
  ]);

  const displayRatings = isFiltered
    ? { ...ratings, reviewsSummary: null }
    : ratings;

  if (isFiltered && ratings.totalReviewCount === 0) {
    return null;
  }

  return (
    <>
      <RatingsAndReviews
        ratings={displayRatings}
        language={webContext.browseLanguage}
        openRatingsModal={openRatingsModal}
        openReviewPolicyModal={openReviewPolicyModal}
        handleReportSuccess={handleReportSuccess}
      />
      <RatingsAndReviewsModal
        open={isRatingsModalOpen}
        ratingsModalFocusOnClose={ratingsModalFocusOnClose}
        closeRatingsModal={closeRatingsModal}
        ratings={displayRatings}
        language={webContext.browseLanguage}
        getReviewsCallback={getReviewsCallback}
        viewMorePending={isViewMorePending}
        filterPending={isFilterPending}
        ratingsApiError={ratingsApiError}
        ratingsApiErrorTranslation={ratingsApiErrorTranslation}
        dismissApiError={dismissApiError}
        filteredReviewCount={filteredReviewCount}
        filterParam={filterParam}
        filterValue={filterValue}
        removeFilter={removeFilter}
        screenHoverable={screenHoverable}
        handleReportSuccess={handleReportSuccess}
      />
      <ReviewPolicyContainer
        open={isReviewPolicyModalOpen}
        reviewPolicyModalFocusOnClose={reviewPolicyModalFocusOnClose}
        closeReviewPolicyModal={closeReviewPolicyModal}
        language={webContext.browseLanguage}
      />
    </>
  );
}

RatingsAndReviewsApp.propTypes = {
  screenHoverable: PropTypes.bool,
};
